import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import { Flex, Text, Table } from "@chakra-ui/react";

import {
    useReactTable,
    getCoreRowModel,
    getExpandedRowModel,
    PaginationState,
    OnChangeFn,
    SortingState
} from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import ClaimsTableRow from "./ClaimsTableRow";
import Pagination from "../../table/Pagination";
import { ClaimParams, ClaimsItemDto } from "@/api/claimsApi";
import { NormalizedSearchResultDto } from "@/api/claimsInvoicingApi";
import TableContent from "../../table/TableContent";
import { useColumns } from "./Columns";
import ClaimsTableToolbar from "./toolbar";

type ClaimsTableProps = {
    searchResult: NormalizedSearchResultDto<ClaimsItemDto> | undefined;
    isClaimsSearchError: boolean;
    isFetchingClaims: boolean;
    claimsQuery: ClaimParams;
    claimsSearchError?: unknown;
    pageIndex: number;
    pageSize: number;
    setClaimsQuery: Dispatch<SetStateAction<ClaimParams>>;
    setPagination: OnChangeFn<PaginationState>;
    sorting: SortingState;
    setSorting: OnChangeFn<SortingState>;
};

const ClaimsTable = ({
    searchResult,
    isClaimsSearchError,
    isFetchingClaims,
    claimsQuery,
    claimsSearchError,
    pageIndex,
    pageSize,
    setClaimsQuery,
    setPagination,
    sorting,
    setSorting
}: ClaimsTableProps) => {
    const { t } = useTranslation("claimsInvoicesOverview");

    const pagination = useMemo(() => ({ pageIndex, pageSize }), [pageIndex, pageSize]);

    const handleSortingChange = useCallback(
        (sortingValue: SortingState | ((old: SortingState) => SortingState)) => {
            setSorting(sortingValue);
            setPagination({ pageIndex: 0, pageSize });
        },
        [setSorting, setPagination, pageSize]
    );

    const resetPagination = useCallback(() => {
        setPagination(p => ({
            pageSize: p.pageSize,
            pageIndex: 0
        }));
    }, [setPagination]);

    const columns = useColumns();

    const table = useReactTable<ClaimsItemDto>({
        columns,
        data: searchResult?.items ?? [],
        pageCount: Math.max(1, Math.ceil((searchResult?.totalResults ?? 0) / pageSize)),
        state: { pagination, sorting },
        onPaginationChange: setPagination,
        onSortingChange: handleSortingChange,
        manualSorting: true,
        manualPagination: true,
        getRowCanExpand: () => true,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        enableSortingRemoval: false
    });

    const rowCount = searchResult?.totalResults ?? 0;

    return (
        <Flex w="100%" gap="2rem" flexDir="column">
            <ClaimsTableToolbar
                isFetchingClaims={isFetchingClaims}
                isClaimsSearchError={isClaimsSearchError}
                claimsQuery={claimsQuery}
                setClaimsQuery={setClaimsQuery}
                resetPagination={resetPagination}
                claimsSearchError={claimsSearchError}
            />
            <Table.ScrollArea
                data-testid="claimsTable"
                overflowY="auto"
                border="2px solid"
                borderRadius="5px"
                borderColor={"anchor.brown.200"}
                maxH="60rem"
            >
                <TableContent table={table}>
                    {table.getRowModel().rows.map(row => (
                        <ClaimsTableRow key={row.original.id} row={row} />
                    ))}
                </TableContent>
                {!searchResult?.items?.length && !isFetchingClaims && (
                    <Text justifySelf="center" padding="1.5rem">
                        {t("emptyClaimsList")}
                    </Text>
                )}
                <Pagination table={table} rowCount={rowCount} id="claimsTable" />
            </Table.ScrollArea>
        </Flex>
    );
};

export default ClaimsTable;
